import { useIsClient } from "@uidotdev/usehooks";
import Head from "next/head";
import { usePathname, useSearchParams } from "next/navigation";
import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as config from "../../../../config/config";
import { jobTypes } from "../../../../helpers/constant";
import { currentMonthYear } from "../../../../helpers/meta_date";

// Set up helper functions to format locations
const getLocationString = (state) => {
  if (!state?.length) return "";

  return state.length > 1
    ? `${state[0]} & ${state.length - 1} other state${state.length > 2 ? "s" : ""
    }`
    : state[0];
};

const market =
  process.env.NEXT_PUBLIC_JSW_GEOLOCATION === "sg" ? "sg" : "my";

const countryName = market === "sg" ? "Singapore" : "Malaysia";
const siteOrigin = `https://${market}.hiredly.com`;

// Filter keywords that have hyphen "-" and capitalize first letter of each word
const filterKeywords = (keywords) => {
  let filteredKeywords = keywords
    ?.replace(/-/g, " ")
    ?.split(" ")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    ?.join(" ");

  return filteredKeywords;
};

// Filter job types from array
const filterJobTypes = (jobType) => {
  const typeMap = jobTypes.reduce((map, type) => {
    // replace hyphen from jobType constant with space
    map[type.id] = type.jobType.replace("-", " ");
    return map;
  }, {});

  const filteredTypes = jobType.map((id) => typeMap[id]).filter(Boolean);

  return filteredTypes.length > 1
    ? `${filteredTypes[0]} & ${filteredTypes.length - 1} more job type${
        filteredTypes.length > 2 ? "s" : ""
      }`
    : filteredTypes[0];
};

const filterSpecialisations = (specialisations, jobSpecialisations) => {
  // Group selected subspecialisations by their parent specialisation
  const groupedByParent = new Map();

  // First pass: Group subspecialisations by their parent specialisation
  jobSpecialisations.forEach((jobSpecialisation) => {
    // Early object destructuring
    const { id, name, subSpecialisations } = jobSpecialisation;
    const selectedSubSpecialisations = subSpecialisations.filter(
      (subSpecialisation) => specialisations.includes(subSpecialisation.id)
    );

    if (selectedSubSpecialisations.length > 0) {
      // Add grouped subspecialisations to map function
      groupedByParent.set(id, {
        parentSpecialisationName: name,
        selected: selectedSubSpecialisations,
        total: subSpecialisations.length,
      });
    }
  });

  // Convert grouped subspecialisations to string
  const filteredSpecialisations = [];
  groupedByParent.forEach(({ parentSpecialisationName, selected, total }) => {
    if (selected.length === total) {
      filteredSpecialisations.push(parentSpecialisationName);
    } else {
      selected.forEach((specialisation) =>
        filteredSpecialisations.push(specialisation.name)
      );
    }
  });

  // Format output
  return filteredSpecialisations.length > 1
    ? `${filteredSpecialisations[0]} & ${
        filteredSpecialisations.length - 1
      } more classification`
    : filteredSpecialisations[0];
};

// meta tags generation function
const generateMetaTags = (filters, specialisationList, stateList) => {
  const defaultMeta = {
    title: `New Job Vacancies in ${countryName} - ${currentMonthYear} | Hiredly`,
    description: `Discover new jobs in ${countryName}, including full-time, internship, and remote opportunities. Filter by role, location, and job type to find your next career move on Hiredly.`,
  };

  const specFilter =
    filters?.specialisation && filters?.specialisation.length > 0;
  const stateFilter = filters?.state && filters?.state.length > 0;
  const jobTypeFilter = filters?.jobType && filters?.jobType.length > 0;
  const keywordFilter = filters?.keyword;

  if (!specFilter && !stateFilter && !jobTypeFilter && !keywordFilter) {
    return defaultMeta;
  }

  const keyword = filters?.keyword
    ? filterKeywords(
        Array.isArray(filters.keyword) ? filters.keyword[0] : filters.keyword
      )
    : null;

  const jobType = filters?.jobType ? filterJobTypes(filters.jobType) : null;
  const specialisation = filters?.specialisation
    ? filterSpecialisations(filters.specialisation, specialisationList)
    : null;
  const location = filters?.state
    ? getLocationString(filters.state, stateList)
    : null;

  // Create an array of object of meta templates to match with condition filters
  const metaTemplates = [
    {
      // Handle multiple filters (4 filters)
      // Filter jobs by keyword, specialization, location and job type
      condition: keyword && specialisation && location && jobType,
      title: `${keyword} ${jobType} in ${specialisation} Job Vacancies in ${location} - ${currentMonthYear} | Hiredly`,
      description: `Explore the latest ${keyword} ${jobType} opportunities in ${specialisation} across ${location} on Hiredly. Compare openings and apply to roles that match your experience.`,
    },
    {
      // Handle multiple filters (3 filters)
      // Filter jobs by keyword, location and specialisation
      condition: keyword && location && specialisation,
      title: `${keyword} in ${specialisation} Job Vacancies in ${location} - ${currentMonthYear} | Hiredly`,
      description: `Browse current ${keyword} opportunities in ${specialisation} across ${location} on Hiredly. Shortlist roles that fit your goals and apply with confidence.`,
    },
    {
      // Filter jobs by keyword, job type and specialisation
      condition: keyword && jobType && specialisation,
      title: `${keyword} ${jobType} in ${specialisation} Job Vacancies in ${countryName} - ${currentMonthYear} | Hiredly`,
      description: `Find current ${keyword} ${jobType} openings in ${specialisation} across ${countryName} on Hiredly. Review requirements and apply to roles suited to your background.`,
    },
    {
      // Filter jobs by keyword, location and job type
      condition: keyword && location && jobType,
      title: `${keyword} ${jobType} Job Vacancies in ${location} - ${currentMonthYear} | Hiredly`,
      description: `Discover current ${keyword} ${jobType} vacancies in ${location} on Hiredly. Compare positions and apply to the opportunities that match your career direction.`,
    },
    {
      // Filter jobs by location, job type and specialisation
      condition: location && jobType && specialisation,
      title: `${jobType} ${specialisation} Job Vacancies in ${location} - ${currentMonthYear} | Hiredly`,
      description: `Explore ${jobType} roles in ${specialisation} across ${location} on Hiredly. Find openings that match your interests and submit your application online.`,
    },
    {
      // Handle multiple filters (2 filters)
      condition: keyword && specialisation,
      title: `${keyword} in ${specialisation} Job Vacancies in ${countryName} - ${currentMonthYear} | Hiredly`,
      description: `Search for ${keyword} roles in ${specialisation} across ${countryName} on Hiredly. Discover relevant job openings and apply to positions aligned with your skills.`,
    },
    {
      condition: keyword && location,
      title: `${keyword} Job Vacancies in ${location} - ${currentMonthYear} | Hiredly`,
      description: `Find the latest ${keyword} job opportunities in ${location} on Hiredly. Filter listings, review key details, and apply to roles that suit your profile.`,
    },
    {
      condition: keyword && jobType,
      title: `${keyword} ${jobType} Job Vacancies in ${countryName} - ${currentMonthYear} | Hiredly`,
      description: `Explore ${countryName}'s latest ${keyword} ${jobType} job vacancies on Hiredly. Discover suitable openings and apply to advance your career.`,
    },
    {
      condition: specialisation && location,
      title: `${specialisation} Job Vacancies in ${location} - ${currentMonthYear} | Hiredly`,
      description: `Discover the latest ${specialisation} job vacancies in ${location} on Hiredly. Compare opportunities and apply to roles that fit your career goals.`,
    },
    {
      condition: specialisation && jobType,
      title: `${jobType} ${specialisation} Job Vacancies in ${countryName} - ${currentMonthYear} | Hiredly`,
      description: `Browse ${jobType} opportunities in ${specialisation} across ${countryName} on Hiredly. Review open positions and apply to jobs that match your experience.`,
    },
    {
      condition: location && jobType,
      title: `${jobType} Job Vacancies in ${location} - ${currentMonthYear} | Hiredly`,
      description: `Search the latest ${jobType} vacancies in ${location} on Hiredly. Identify relevant roles quickly and apply to your preferred openings.`,
    },
    {
      // Handle single filter
      condition: keyword,
      title: `${keyword} Job Vacancies in ${countryName} - ${currentMonthYear} | Hiredly`,
      description: `Find your next ${keyword} job in ${countryName} on Hiredly. Explore current vacancies, evaluate role details, and apply to opportunities that fit your goals.`,
    },
    {
      condition: specialisation,
      title: `${specialisation} Job Vacancies in ${countryName} - ${currentMonthYear} | Hiredly`,
      description: `Explore current ${specialisation} job vacancies across ${countryName} on Hiredly. Discover matching roles and apply to accelerate your next career step.`,
    },
    {
      condition: jobType,
      title: `${jobType} Job Vacancies in ${countryName} - ${currentMonthYear} | Hiredly`,
      description: `Discover the latest ${jobType} job vacancies in ${countryName} on Hiredly. Browse fresh openings and apply to positions that match your preferred work style.`,
    },
    {
      condition: location,
      title: `Job Vacancies in ${location} - ${currentMonthYear} | Hiredly`,
      description: `Find the latest job vacancies in ${location} on Hiredly. Explore active openings by industry and apply to the roles best suited to your profile.`,
    },
  ];

  // Find the first matching template
  const matchedTemplate = metaTemplates.find((template) => {
    return template.condition;
  });

  return matchedTemplate || defaultMeta;
};

function SharedJobListHeader({ ssrFilterParams, specialisationList, jobs }) {
  const jobsParams = useSelector((state) => state.jobs?.jobListFilter);
  const stateList = useSelector((state) => state.jobs?.stateRegions);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isClient = useIsClient();

  // Track both SSR and client-side filter params
  const [currentFilter, setCurrentFilter] = useState(ssrFilterParams);

  useEffect(() => {
    if (isClient) {
      setCurrentFilter(jobsParams);
    }
  }, [isClient]);

  useEffect(() => {
    setCurrentFilter(jobsParams);
  }, [jobsParams]);

  // generate meta tags
  const { title: metaTitle, description: metaDescription } = generateMetaTags(
    currentFilter,
    specialisationList,
    stateList
  );

  let search = new URLSearchParams(searchParams);
  // remove filter from search params
  search.delete("filter");

  let metaIndex = false;
  let canonicalUrl = "";

  if (pathname.includes("jobs")) {
    if (search.toString() === "") {
      canonicalUrl = `${siteOrigin}${pathname}`;
    } else {
      canonicalUrl = `${siteOrigin}${pathname}?${search.toString()}`;
    }
  } else {
    canonicalUrl = `${siteOrigin}/jobs`;
  }

  if (jobs.length === 0) {
    metaIndex = true;
  }

  return (
    <Fragment>
      <Head>
        {/* Primary Meta Tags */}
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />

        {metaIndex && <meta name="robots" content="noindex, nofollow" />}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" key={"ogType"} />
        <meta property="og:url" content={canonicalUrl} key="ogUrl" />
        <meta property="og:title" content={metaTitle} key="ogTitle" />
        <meta
          property="og:description"
          content={metaDescription}
          key="ogDescription"
        />
        <meta
          property="og:image"
          content={
            config.assetDomain + "/images/hiredly/hiredly-meta-image-v1.png"
          }
          key="ogImage"
        />

        {/* Twitter */}
        <meta
          property="twitter:card"
          content="summary_large_image"
          key="twitterCard"
        />
        <meta property="twitter:url" content={canonicalUrl} key="twitterUrl" />
        <meta property="twitter:title" content={metaTitle} key="twitterTitle" />
        <meta
          property="twitter:description"
          content={metaDescription}
          key="twitterDescription"
        />
        <meta
          property="twitter:image"
          content={
            config.assetDomain + "/images/hiredly/hiredly-meta-image-v1.png"
          }
          key="twitterImage"
        />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
      </Head>
    </Fragment>
  );
}

export default SharedJobListHeader;
